// Phone check (SICKW) and unlock sites: the checks the counter can run, how an IMEI or
// serial is read, and the unlock site list. Shared by the server and the screens.
import { isValidImei } from './format.js';

export type SickwGroup = 'apple' | 'carrier' | 'android' | 'other';

export interface SickwService {
  /** SICKW's service number. */
  id: string;
  name: string;
  group: SickwGroup;
  /** SICKW's list price in US dollars ("0.00" is free). What you pay shows after each check. */
  price: string | null;
  /** Short name for the quick buttons; only the most used checks have one. */
  quick?: string;
}

export const SICKW_GROUP_LABELS: Record<SickwGroup, string> = {
  apple: 'Apple',
  carrier: 'Carrier & blacklist',
  android: 'Samsung & other Android',
  other: 'Other',
};

/**
 * Checks only: SICKW's unlock orders and premium services (MDM bypass, carrier unlocks
 * that cost $2 to $165) are left out on purpose, so nobody orders one by mistake.
 * Prices are SICKW's list prices (October 2026); SICKW charges your account its own price.
 */
export const SICKW_SERVICES: SickwService[] = [
  // Apple
  { id: '3', name: 'iCloud ON/OFF', group: 'apple', price: '0.00', quick: 'iCloud ON/OFF' },
  { id: '61', name: 'iPhone carrier + Find My + blacklist', group: 'apple', price: '0.10', quick: 'iPhone carrier, FMI, blacklist' },
  { id: '78', name: 'iPhone carrier + Find My', group: 'apple', price: '0.08' },
  { id: '103', name: 'iPhone carrier', group: 'apple', price: '0.06' },
  { id: '147', name: 'iPhone carrier + Find My + blacklist (S2)', group: 'apple', price: '0.25' },
  { id: '151', name: 'iPhone carrier + Chimaera detect', group: 'apple', price: '0.12' },
  { id: '72', name: 'Apple carrier + MDM + iCloud + GSX', group: 'apple', price: '0.40' },
  { id: '66', name: 'iPhone & Mac iCloud clean/lost', group: 'apple', price: '0.22' },
  { id: '110', name: 'MacBook & iMac iCloud ON/OFF', group: 'apple', price: '0.20' },
  { id: '40', name: 'Apple MDM + iCloud', group: 'apple', price: '0.24' },
  { id: '81', name: 'Apple MDM status', group: 'apple', price: '0.30' },
  { id: '101', name: 'Apple activation status', group: 'apple', price: '0.03' },
  { id: '88', name: 'Apple activation status (Pro)', group: 'apple', price: '0.04' },
  { id: '26', name: 'Apple serial info', group: 'apple', price: '0.00' },
  { id: '152', name: 'Apple warranty (by serial)', group: 'apple', price: '0.00' },
  { id: '92', name: 'iPhone model, color & capacity', group: 'apple', price: '0.022' },
  { id: '219', name: 'Apple part number (MPN)', group: 'apple', price: '0.15' },
  { id: '12', name: 'IMEI to serial (and back)', group: 'apple', price: '0.025' },
  { id: '77', name: 'Apple GSX repair eligibility', group: 'apple', price: '0.14' },
  { id: '29', name: 'Apple replacement history', group: 'apple', price: '0.70' },
  { id: '146', name: 'Apple GSX premium (promo)', group: 'apple', price: '0.80' },
  { id: '68', name: 'Apple GSX cases & repairs', group: 'apple', price: '1.20' },
  { id: '105', name: 'Apple sold-by & country', group: 'apple', price: '1.80' },
  { id: '63', name: 'Apple GSX premium details', group: 'apple', price: '2.00' },
  { id: '60', name: 'Apple iCloud ID hint', group: 'apple', price: '0.40' },
  { id: '85', name: 'Apple demo device info', group: 'apple', price: '0.20' },
  // Carrier status and blacklist
  { id: '54', name: 'Blacklist (worldwide)', group: 'carrier', price: '0.04', quick: 'Blacklist' },
  { id: '16', name: 'T-Mobile USA status (Pro)', group: 'carrier', price: '0.05', quick: 'T-Mobile' },
  { id: '65', name: 'AT&T USA status & unlock', group: 'carrier', price: '0.40', quick: 'AT&T' },
  { id: '232', name: 'Verizon USA status (Pro)', group: 'carrier', price: '0.07', quick: 'Verizon' },
  { id: '21', name: 'Cricket USA status (Pro)', group: 'carrier', price: '0.15' },
  { id: '220', name: 'Tracfone USA status (Pro)', group: 'carrier', price: '0.06' },
  { id: '230', name: 'Xfinity USA status (Pro)', group: 'carrier', price: '0.08' },
  { id: '231', name: 'C Spire USA status (Pro)', group: 'carrier', price: '0.08' },
  // Samsung and other Android
  { id: '80', name: 'Samsung info', group: 'android', price: '0.00', quick: 'Samsung info' },
  { id: '1', name: 'Samsung info (Pro)', group: 'android', price: '0.10' },
  { id: '82', name: 'Samsung Knox Guard', group: 'android', price: '0.30', quick: 'Knox Guard' },
  { id: '42', name: 'Google Pixel info', group: 'android', price: '0.12', quick: 'Pixel' },
  { id: '13', name: 'Motorola info', group: 'android', price: '0.00' },
  { id: '233', name: 'Nothing Phone info', group: 'android', price: '0.00' },
  { id: '17', name: 'Alcatel info', group: 'android', price: '0.00' },
  { id: '43', name: 'Kyocera info', group: 'android', price: '0.00' },
  { id: '22', name: 'Lenovo info', group: 'android', price: '0.00' },
  { id: '55', name: 'ZTE, Nubia & RedMagic info', group: 'android', price: '0.00' },
  { id: '34', name: 'Asus info', group: 'android', price: '0.00' },
  { id: '15', name: 'Huawei info', group: 'android', price: '0.00' },
  { id: '73', name: 'Honor info', group: 'android', price: '0.00' },
  { id: '75', name: 'Vivo & iQOO info', group: 'android', price: '0.00' },
  { id: '153', name: 'Xiaomi, Redmi & Poco warranty', group: 'android', price: '0.10' },
  { id: '206', name: 'Xiaomi Mi Lock ON/OFF', group: 'android', price: '0.10' },
  { id: '39', name: 'Oppo, OnePlus & Realme (Europe)', group: 'android', price: '0.20' },
  { id: '203', name: 'Brand & model (any phone)', group: 'android', price: '0.00' },
];

/** The checks staff can run: the list above, plus the buy-phone checks from Settings that aren't in it. */
export function sickwCatalog(buyServiceIds?: string | readonly string[] | null): SickwService[] {
  const ids = (typeof buyServiceIds === 'string' || !buyServiceIds ? [buyServiceIds ?? ''] : [...buyServiceIds]).map((x) => x.trim()).filter(Boolean);
  const extra = [...new Set(ids)].filter((id) => !SICKW_SERVICES.some((s) => s.id === id));
  if (!extra.length) return SICKW_SERVICES;
  return [...SICKW_SERVICES, ...extra.map((id): SickwService => ({ id, name: `Buy-phone check (SICKW #${id})`, group: 'other', price: null }))];
}

/** "$0.10", "Free", or "" when the price isn't known. */
export function sickwPriceLabel(price: string | null | undefined): string {
  if (price === null || price === undefined || price === '') return '';
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return 'Free';
  // Some checks cost a fraction of a cent ($0.022); show that third digit only when it's there.
  const three = n.toFixed(3);
  return `$${three.endsWith('0') ? n.toFixed(2) : three}`;
}

/** What staff type or scan: spaces, dashes and slashes go, letters become capitals. */
export function cleanDeviceId(raw: string | null | undefined): string {
  return (raw ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** Why an IMEI or serial can't be checked, or null when it can. */
export function deviceIdProblem(id: string): string | null {
  if (!id) return 'Type or scan the IMEI (dial *#06#) or the serial number.';
  if (/^\d{15}$/.test(id)) return isValidImei(id) ? null : 'That IMEI doesn’t add up. Check for a typo (dial *#06# to see it).';
  if (/^\d{16,}$/.test(id)) return 'IMEIs are 15 digits. Check for an extra digit.';
  if (id.length < 10 || id.length > 15) return 'Enter the 15-digit IMEI, or the serial number (10 to 15 letters and numbers).';
  return null;
}

// ---------------------------------------------------------------- unlock sites

export interface UnlockSite {
  name: string;
  /** Null until the owner adds the link in Settings. */
  url: string | null;
}

export const DEFAULT_UNLOCK_SITES = 'GSM Unlockers | https://www.gsmunlockers.com/\nAT&T unlock | https://www.att.com/deviceunlock/\nHankUnlock | https://www.hangunlock.com/orders/imei/status/all';

const MAX_SITES = 12;

/** Reads the Settings list: one site per line, "Name | https://link". */
export function parseUnlockSites(text: string | null | undefined): UnlockSite[] {
  return (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf('|');
      const name = (i >= 0 ? line.slice(0, i) : line).trim();
      const url = i >= 0 ? line.slice(i + 1).trim() : '';
      return { name, url: /^https:\/\/[^\s|]+$/.test(url) ? url : null };
    })
    .filter((s) => s.name.length >= 2)
    .slice(0, MAX_SITES);
}

/** Why the Settings list can't be saved, or null when it's fine. */
export function unlockSitesProblem(text: string): string | null {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length > MAX_SITES) return `Add up to ${MAX_SITES} unlock sites.`;
  for (const line of lines) {
    const m = /^([^|]{2,40})\|\s*(\S*)\s*$/.exec(line);
    if (!m) return `Write each unlock site as "Name | https://link", one per line. Check "${line.slice(0, 40)}".`;
    if (m[2] && !/^https:\/\/[^\s|]+$/.test(m[2])) return `The link for ${m[1].trim()} has to start with https://`;
  }
  return null;
}
